import React, { useState, useEffect, useMemo } from 'react';
import { getServicesManager } from 'services';
import { Typography } from 'interfaces/typography';
import { PrimaryActionButton, STANDARD_CONTAINER_CLASSES } from 'utils/CommonUIComponents';
import { TeamInvitation } from "interfaces/db";
import { uniqueBy } from "core";
import { Mail, UserPlus, X, Edit3, Users, Copy, Check } from 'lucide-react';
import { Box, Modal } from '@mui/material';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { useDispatch } from 'react-redux';
import { updateTeam } from 'lib/redux/users/slice';

export function TeamInvitations() {
  const dispatch = useDispatch();
  const { team, isAdmin } = useUserData();
  
  // Team invitation states
  const [sentInvitations, setSentInvitations] = useState<TeamInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteCanDial, setInviteCanDial] = useState<boolean>(true);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  
  // Bulk invitation states
  const [bulkInviteEmails, setBulkInviteEmails] = useState<string>('');
  const [bulkInviteCanDial, setBulkInviteCanDial] = useState<boolean>(true);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState<boolean>(false);
  const [showBulkInviteConfirm, setShowBulkInviteConfirm] = useState<boolean>(false);
  const [parsedEmails, setParsedEmails] = useState<string[]>([]);
  
  // Team rename states
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [renamingTeam, setRenamingTeam] = useState<boolean>(false);
  const [copiedInvitationEmail, setCopiedInvitationEmail] = useState<string | null>(null);
  const teamName = team?.team ?? '';

  useEffect(() => {
    setNewTeamName(teamName);
  }, [teamName]);

  useEffect(() => {
    // Load sent invitations
    getServicesManager().getSentTeamInvitations().then(sentInvitations => {
      if (sentInvitations) { setSentInvitations(sentInvitations) }
    })
  }, [team?.team_id])

  // Bulk invite helpers
  const parseEmailsFromText = (text: string): string[] => {
    const emails = text
      .split(/[,;\n\r\t\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@'))
      .filter((email, index, array) => array.indexOf(email) === index);
    return emails;
  };

  const handleCopyInvitationLink = async (invitation: TeamInvitation) => {
    const hostname = window.location.origin;
    const invitationLink = `${hostname}/invitations?email=${encodeURIComponent(invitation.email)}&team_id=${encodeURIComponent(invitation.team_id)}`;
    
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopiedInvitationEmail(invitation.email);
      setTimeout(() => setCopiedInvitationEmail(null), 2000);
    } catch (err) {
      console.error('Failed to copy invitation link:', err);
    }
  };

  const handleBulkInviteSubmit = () => {
    const emails = parseEmailsFromText(bulkInviteEmails);
    setParsedEmails(emails);
    if (emails.length > 0) {
      setShowBulkInviteConfirm(true);
    }
  };

  const confirmBulkInvite = async () => {
    const promises = parsedEmails.map(email => 
      getServicesManager().createTeamInvitation(email, bulkInviteCanDial)
    );
    
    const results = await Promise.allSettled(promises);
    const successful = results
      .map((result) => result.status === 'fulfilled' && result.value ? result.value : null)
      .filter((invitation): invitation is TeamInvitation => invitation !== null);
    
    if (successful.length > 0) {
      setSentInvitations(sentInvitations => 
        uniqueBy([...sentInvitations, ...successful], i => i.email)
      );
    }
    
    setBulkInviteEmails('');
    setBulkInviteCanDial(true);
    setShowBulkInviteModal(false);
    setShowBulkInviteConfirm(false);
    setParsedEmails([]);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Current Team Section */}
      <div className={STANDARD_CONTAINER_CLASSES} style={{ borderRadius: '8px', padding: '1.5rem' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                Current Team
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280">
                Manage your team settings and invitations
              </Typography>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowRenameModal(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
            >
              <Edit3 size={14} />
              Edit
            </button>
          )}
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
              <Typography variant="mediumParagraph" color="#374151" fontWeight={600}>
                {teamName.charAt(0).toUpperCase()}
              </Typography>
            </div>
            <div>
              <Typography variant="h5" color="#1F2937" fontWeight={600}>
                {teamName || 'Unnamed Team'}
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280">
                Team configuration and settings
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {/* Sent Invitations Section */}
      <div className={STANDARD_CONTAINER_CLASSES} style={{ borderRadius: '8px', padding: '1.5rem' }}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Mail size={20} className="text-gray-700" />
            <Typography variant="h4" color="#1F2937" fontWeight={600}>
              Invitations
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!isAdmin || team?.subscription_type !== 'TEAM') {
                  setShowUpgradeModal(true);
                } else {
                  setShowInviteModal(true);
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <div className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full">
                <UserPlus size={14} />
              </div>
              <Typography variant="mediumParagraph" color="white" fontWeight={500}>
                Send Invitation
              </Typography>
            </button>
            <button
              onClick={() => {
                if (!isAdmin || team?.subscription_type !== 'TEAM') {
                  setShowUpgradeModal(true);
                } else {
                  setShowBulkInviteModal(true);
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-md transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <div className="flex items-center justify-center w-5 h-5">
                <Mail size={14} />
              </div>
              <Typography variant="mediumParagraph" color="inherit" fontWeight={500}>
                Bulk Invite
              </Typography>
            </button>
          </div>
        </div>
      
        {sentInvitations.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Mail size={24} className="text-gray-400" />
            </div>
            <Typography variant="mediumParagraph" color="#6B7280" style={{ marginBottom: '0.5rem' }}>
              No invitations sent yet
            </Typography>
            <Typography variant="smallParagraph" color="#9CA3AF">
              Start by sending your first team invitation
            </Typography>
          </div>
        ) : (
          <div className="space-y-3">
            {sentInvitations.map(invitation => (
              <div key={invitation.email} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500} style={{ marginBottom: '0.25rem' }}>
                      {invitation.email}
                    </Typography>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        invitation.can_dial ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {invitation.can_dial ? 'Can Dial' : 'View Only'}
                      </span>
                      <Typography variant="smallParagraph" color="#6B7280">
                        Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                      </Typography>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleCopyInvitationLink(invitation)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    {copiedInvitationEmail === invitation.email ? (
                      <>
                        <Check size={14} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      getServicesManager().rescindTeamInvitation(invitation.email).then(numDeleted => {
                        if (numDeleted !== null) {
                          setSentInvitations(sentInvitations => 
                            sentInvitations.filter(i => i.email !== invitation.email)
                          );
                        }
                      });
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <X size={14} />
                    Rescind
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Invite Modal */}
      <Modal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '450px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserPlus size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Send Team Invitation
                </Typography>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Email Address
              </Typography>
              <input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inviteCanDial}
                  onChange={(e) => setInviteCanDial(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.25rem' }}>
                    Can dial (grant phone access)
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Allow this user to make phone calls through the platform
                  </Typography>
                </div>
              </label>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowInviteModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={<Mail size={16} />}
              onClick={() => {
                if (inviteEmail.trim()) {
                  getServicesManager().createTeamInvitation(inviteEmail, inviteCanDial).then(newInvitation => {
                    if (newInvitation) {
                      setSentInvitations(sentInvitations => uniqueBy([...sentInvitations, newInvitation], i => i.email));
                      setInviteEmail('');
                      setInviteCanDial(true);
                      setShowInviteModal(false);
                    }
                  });
                }
              }}
              disabled={!inviteEmail.trim()}
            >
              Send Invitation
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
      
      {/* Upgrade Modal */}
      <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <UserPlus size={16} className="text-white" />
                </div>
                <Typography variant="h4" color="white" fontWeight={600}>
                  Upgrade to Team Account
                </Typography>
              </div>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <UserPlus size={24} className="text-blue-600" />
              </div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Team Features Required
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                To invite team members, you need to upgrade to a team account.
              </Typography>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.75rem' }}>
                Team Account Benefits:
              </Typography>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Send team invitations
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Manage user permissions
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Advanced team analytics
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Priority support
                  </Typography>
                </li>
              </ul>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <Typography variant="mediumParagraph" color="#1e40af" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Ready to upgrade?
              </Typography>
              <Typography variant="smallParagraph" color="#374151">
                Contact our team at{' '}
                <a 
                  href="mailto:founders@trellus.ai" 
                  className="text-blue-600 hover:text-blue-700 font-medium underline"
                >
                  founders@trellus.ai
                </a>
                {' '}to activate your team account.
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Maybe Later
            </button>
            <a
              href="mailto:founders@trellus.ai?subject=Team Account Upgrade Request"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
            >
              <Mail size={16} />
              Contact Team
            </a>
          </div>
        </Box>
      </Modal>
      
      {/* Bulk Invite Modal */}
      <Modal
        open={showBulkInviteModal}
        onClose={() => setShowBulkInviteModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Bulk Team Invitations
                </Typography>
              </div>
              <button
                onClick={() => setShowBulkInviteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Email Addresses
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280" style={{ marginBottom: '1rem' }}>
                Enter multiple email addresses separated by commas, spaces, or on new lines
              </Typography>
              <textarea
                placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
                value={bulkInviteEmails}
                onChange={(e) => setBulkInviteEmails(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151',
                  minHeight: '120px'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkInviteCanDial}
                  onChange={(e) => setBulkInviteCanDial(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.25rem' }}>
                    Can dial (grant phone access)
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Allow all invited users to make phone calls through the platform
                  </Typography>
                </div>
              </label>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowBulkInviteModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={<UserPlus size={16} />}
              onClick={handleBulkInviteSubmit}
              disabled={!bulkInviteEmails.trim()}
            >
              Review Invitations
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
      
      {/* Bulk Invite Confirmation Modal */}
      <Modal
        open={showBulkInviteConfirm}
        onClose={() => setShowBulkInviteConfirm(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-blue-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Mail size={16} className="text-white" />
                </div>
                <Typography variant="h4" color="white" fontWeight={600}>
                  Confirm Bulk Invitations
                </Typography>
              </div>
              <button
                onClick={() => setShowBulkInviteConfirm(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail size={24} className="text-blue-600" />
              </div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Send {parsedEmails.length} Invitation{parsedEmails.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                Review the email addresses below and confirm to send team invitations:
              </Typography>
            </div>
            
            {/* Email list */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-60 overflow-y-auto">
              <div className="space-y-2">
                {parsedEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-white rounded border">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail size={14} className="text-blue-600" />
                    </div>
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                      {email}
                    </Typography>
                    <div className="ml-auto">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        bulkInviteCanDial ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {bulkInviteCanDial ? 'Can Dial' : 'View Only'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Typography variant="mediumParagraph" color="#1e40af" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Double-check before sending
              </Typography>
              <Typography variant="smallParagraph" color="#374151">
                Each person will receive an email invitation to join your team with the access level shown above.
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setShowBulkInviteConfirm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Back to Edit
            </button>
            <PrimaryActionButton
              icon={<Mail size={16} />}
              onClick={confirmBulkInvite}
            >
              Send {parsedEmails.length} Invitation{parsedEmails.length !== 1 ? 's' : ''}
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
      
      {/* Team Rename Modal */}
      <Modal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '450px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Edit3 size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Rename Team
                </Typography>
              </div>
              <button
                onClick={() => setShowRenameModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-4">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Team Name
              </Typography>
              <input
                type="text"
                placeholder="Enter new team name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="smallParagraph" color="#6B7280">
                This will update your team name for all members. Current name: <strong>{teamName}</strong>
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowRenameModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={renamingTeam ? null : <Edit3 size={16} />}
              onClick={() => {
                if (newTeamName.trim() && newTeamName.trim() !== teamName) {
                  setRenamingTeam(true);
                  getServicesManager().postSettings({is_team: true, team: newTeamName.trim()}).then(() => {
                    setShowRenameModal(false);
                    if (team) {
                      dispatch(updateTeam({ ...team, team: newTeamName.trim() }));
                    }
                  }).finally(() => {
                    setRenamingTeam(false);
                  });
                }
              }}
              disabled={!newTeamName.trim() || newTeamName.trim() === teamName || renamingTeam}
            >
              {renamingTeam ? (
                <div className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Renaming...
                </div>
              ) : (
                'Rename Team'
              )}
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
    </div>
  );
}